import React, { useRef, useEffect } from 'react';
import Hls from 'hls.js';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getFileServerBase } from './fileServerBase';

// ─── Media Helper ─────────────────────────────────────────────────────────────

const IMAGE_BASE = `${getFileServerBase()}/media/large/`;
const VIDEO_BASE = `${getFileServerBase()}/media/video/`;

const resolveMediaUrl = (url: string | null | undefined, isVideo = false): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return isVideo ? `${VIDEO_BASE}${url}` : `${IMAGE_BASE}${url}`;
};

// ─── Font Family Map ──────────────────────────────────────────────────────────

const FONT_FAMILY_MAP: Record<string, string> = {
  inter:        '"Inter", sans-serif',
  perpetua:     '"Perpetua", "Georgia", serif',
  'sans-serif': 'sans-serif',
};

const getFontFamily = (value?: string): string =>
  FONT_FAMILY_MAP[value ?? 'inter'] ?? '"Inter", sans-serif';

// ─── Responsive Font Size ─────────────────────────────────────────────────────

const getMobileFontSize = (desktopSize: number): number => {
  if (desktopSize > 40) return Math.round(desktopSize * 0.79); // 56 → 44, 48 → 38
  if (desktopSize > 24) return Math.round(desktopSize * 0.75); // 32 → 24, 28 → 21
  if (desktopSize > 18) return Math.round(desktopSize * 0.8);  // 20 → 16
  return desktopSize;
};

// ─── Layout Map ───────────────────────────────────────────────────────────────

const LAYOUT_COLUMNS: Record<string, string> = {
  '1col':                '1fr',
  '1col_full':           '1fr',
  '2col_equal':          '1fr 1fr',
  '2col_offset':         '2fr 1fr',
  '2col_offset_reverse': '1fr 2fr',
  '3col_equal':          '1fr 1fr 1fr',
  '3col_offset':         '2fr 1fr 1fr',
  '3col_offset_reverse': '1fr 1fr 2fr',
  '4col_equal':          '1fr 1fr 1fr 1fr',
};

// ─── Widget Renderers ─────────────────────────────────────────────────────────

interface WidgetProps {
  config: Record<string, any>;
}

const VideoImageTileWidget: React.FC<WidgetProps> = ({ config }) => {
  const navigate = useNavigate();
  const {
    mediaUrl,
    mediaType,
    mediaLinkUrl = '',
    aspectRatio = '4/3',
    mediaFit = 'cover',
    mediaObjectPosition = 'center',
    mediaBorderRadius = 0,
    hoverAnimationEnabled = true,
  } = config;

  const handleLinkClick = () => {
    if (!mediaLinkUrl) return;
    if (mediaLinkUrl.startsWith('http://') || mediaLinkUrl.startsWith('https://')) {
      window.open(mediaLinkUrl, '_blank');
    } else {
      navigate(mediaLinkUrl);
    }
  };

  const isVideo =
    mediaType === 'video' ||
    (!mediaType && !!mediaUrl?.match(/\.(mp4|mov|webm|m3u8)$/i));

  const resolvedUrl = resolveMediaUrl(mediaUrl, isVideo);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef   = useRef<Hls | null>(null);

  useEffect(() => {
    if (!isVideo || !resolvedUrl || !videoRef.current) return;
    const video = videoRef.current;

    // Destroy any previous HLS instance
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }

    if (resolvedUrl.includes('.m3u8')) {
      if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
        hlsRef.current = hls;
        hls.loadSource(resolvedUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal) {
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
            else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
            else hls.destroy();
          }
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS (Safari)
        video.src = resolvedUrl;
      }
    } else {
      video.src = resolvedUrl;
    }

    return () => { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } };
  }, [resolvedUrl, isVideo]);

  if (!resolvedUrl) return null;

  // Hover-zoom only applies to the image case — video already auto-plays/loops,
  // so a hover-triggered transform on it would be visually inconsistent.
  const containerSx = {
    width: '100%',
    height: '100%',
    position: 'relative' as const,
    aspectRatio: aspectRatio !== 'auto' ? aspectRatio : undefined,
    overflow: 'hidden',
    borderRadius: mediaBorderRadius > 0 ? `${mediaBorderRadius}px` : undefined,
    ...(!isVideo && hoverAnimationEnabled ? { '&:hover img': { transform: 'scale(1.03)' } } : {}),
  };

  const linkSx = mediaLinkUrl
    ? { cursor: 'pointer', display: 'block', width: '100%', height: '100%' }
    : {};

  if (isVideo) {
    return (
      <Box sx={linkSx} onClick={mediaLinkUrl ? handleLinkClick : undefined}>
        <Box sx={containerSx}>
          <video
            ref={videoRef}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            autoPlay muted loop playsInline
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={linkSx} onClick={mediaLinkUrl ? handleLinkClick : undefined}>
      <Box sx={containerSx}>
        <img
          src={resolvedUrl}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: mediaFit as any,
            objectPosition: mediaObjectPosition,
            display: 'block',
            transition: hoverAnimationEnabled ? 'transform 0.5s ease' : undefined,
          }}
          loading="lazy"
        />
      </Box>
    </Box>
  );
};

const TextBoxBodyWidget: React.FC<WidgetProps> = ({ config }) => {
  const {
    body = '',
    bodyAlign = 'left',
    bodyFontFamily = 'inter',
    bodyFontSize = 16,
    bodyFontWeight = 400,
    bodyItalic = false,
    bodyLineHeight = 1.6,
    bodyTextTransform = 'none',
    bodyColor = '',
    paddingTop = 0,
    paddingBottom = 0,
    paddingLeft = 0,
    paddingRight = 0,
  } = config;

  if (!body) return null;

  const mobileFontSize = getMobileFontSize(bodyFontSize);

  return (
    <Typography
      sx={{
        fontFamily: getFontFamily(bodyFontFamily),
        fontSize: { xs: `${mobileFontSize}px`, md: `${bodyFontSize}px` },
        fontWeight: bodyFontWeight,
        fontStyle: bodyItalic ? 'italic' : 'normal',
        textAlign: bodyAlign,
        lineHeight: bodyLineHeight,
        textTransform: bodyTextTransform,
        color: bodyColor || 'inherit',
        whiteSpace: 'pre-wrap',
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
        paddingLeft: `${paddingLeft}px`,
        paddingRight: `${paddingRight}px`,
        width: '100%',
      }}
    >
      {body}
    </Typography>
  );
};

const TextBoxHeadingWidget: React.FC<WidgetProps> = ({ config }) => {
  const {
    heading = '',
    headingAlign = 'left',
    headingBold = false,
    headingFontSize = 24,
    paddingTop = 0,
    paddingBottom = 0,
  } = config;

  if (!heading) return null;

  const mobileHeadingSize = getMobileFontSize(headingFontSize);

  return (
    <Typography
      variant="h4"
      sx={{
        fontSize: { xs: `${mobileHeadingSize}px`, md: `${headingFontSize}px` },
        fontWeight: headingBold ? 700 : 500,
        textAlign: headingAlign,
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
        width: '100%',
      }}
    >
      {heading}
    </Typography>
  );
};

const ButtonWidget: React.FC<WidgetProps> = ({ config }) => {
  const navigate = useNavigate();
  const {
    label = '',
    url = '',
    variant = 'outlined',
    buttonSize = 'medium',
    buttonAlign = 'left',
    buttonFontFamily = 'inter',
    buttonFontWeight = 400,
    buttonTextTransform = 'uppercase',
    buttonBorderRadius = 0,
    buttonMinWidth = 0,
    buttonFullWidth = false,
    paddingTop = 0,
  } = config;

  if (!label) return null;

  const handleClick = () => {
    if (!url) return;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  const justifyMap: Record<string, string> = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: justifyMap[buttonAlign] ?? 'flex-start',
        width: '100%',
        paddingTop: `${paddingTop}px`,
      }}
    >
      <Button
        variant={variant as any}
        size={buttonSize as any}
        onClick={handleClick}
        disableElevation
        sx={{
          fontFamily: getFontFamily(buttonFontFamily),
          fontWeight: buttonFontWeight,
          textTransform: buttonTextTransform,
          borderRadius: `${buttonBorderRadius}px`,
          minWidth: buttonMinWidth > 0 ? `${buttonMinWidth}px` : undefined,
          width: buttonFullWidth ? '100%' : undefined,
        }}
      >
        {label}
      </Button>
    </Box>
  );
};

const SpacerWidget: React.FC<WidgetProps> = ({ config }) => {
  const { height = 40 } = config;
  return <Box sx={{ height: `${height}px`, width: '100%' }} />;
};

const HorizontalRuleWidget: React.FC<WidgetProps> = ({ config }) => {
  const {
    ruleWidth = 100,
    ruleThickness = 1,
    ruleStyle = 'solid',
    ruleColor = '#000000',
    paddingTop = 0,
    paddingBottom = 0,
    paddingLeft = 0,
    paddingRight = 0,
  } = config;
  return (
    <Box
      sx={{
        width: '100%',
        pt: `${paddingTop}px`,
        pb: `${paddingBottom}px`,
        pl: `${paddingLeft}px`,
        pr: `${paddingRight}px`,
      }}
    >
      <Box
        sx={{
          width: `${ruleWidth}%`,
          mx: 'auto',
          borderTopWidth: `${ruleThickness}px`,
          borderTopStyle: ruleStyle,
          borderTopColor: ruleColor || '#000000',
        }}
      />
    </Box>
  );
};

// ─── Widget Dispatcher ────────────────────────────────────────────────────────

interface Widget {
  id: string;
  type: string;
  config: Record<string, any>;
}

const renderWidget = (widget: Widget) => {
  switch (widget.type) {
    case 'video_image_tile':
      return <VideoImageTileWidget key={widget.id} config={widget.config} />;
    case 'text_box_body':
      return <TextBoxBodyWidget key={widget.id} config={widget.config} />;
    case 'text_box_heading':
      return <TextBoxHeadingWidget key={widget.id} config={widget.config} />;
    case 'button':
      return <ButtonWidget key={widget.id} config={widget.config} />;
    case 'spacer':
      return <SpacerWidget key={widget.id} config={widget.config} />;
    case 'horizontal_rule':
      return <HorizontalRuleWidget key={widget.id} config={widget.config} />;
    default:
      return null;
  }
};

// ─── Slot Renderer ────────────────────────────────────────────────────────────

interface SlotConfig {
  aspectRatio?: string;
  mobileAspectRatio?: string;
  gap?: number;
  mobileGap?: number;
  horizontalAlign?: string;
  verticalAlign?: string;
  padding?: number;
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  mobilePadding?: number;
  maxWidth?: number;
  objectFit?: string;
  mobileHidden?: boolean;
  mobileOrder?: number;
}

interface Slot {
  id: string;
  slotConfig: SlotConfig;
  widgets: Widget[];
}

const SlotRenderer: React.FC<{ slot: Slot }> = ({ slot }) => {
  const { slotConfig, widgets } = slot;

  const {
    gap = 0,
    mobileGap = 0,
    horizontalAlign = 'left',
    verticalAlign = 'top',
    padding = 0,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    mobilePadding = 0,
    maxWidth = 0,
    mobileHidden = false,
    mobileOrder = 0,
    aspectRatio = 'auto',
    mobileAspectRatio = 'auto',
  } = slotConfig;

  const pt = paddingTop    ?? padding;
  const pb = paddingBottom ?? padding;
  const pl = paddingLeft   ?? padding;
  const pr = paddingRight  ?? padding;

  const justifyMap: Record<string, string> = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  const alignMap: Record<string, string> = {
    top: 'flex-start',
    center: 'center',
    bottom: 'flex-end',
  };

  return (
    <Box
      sx={{
        display: mobileHidden ? { xs: 'none', md: 'flex' } : 'flex',
        flexDirection: 'column',
        gap: { xs: `${mobileGap}px`, md: `${gap}px` },
        justifyContent: alignMap[verticalAlign] ?? 'flex-start',
        alignItems: justifyMap[horizontalAlign] ?? 'flex-start',
        paddingTop:    { xs: `${mobilePadding}px`, md: `${pt}px` },
        paddingBottom: { xs: `${mobilePadding}px`, md: `${pb}px` },
        paddingLeft:   { xs: `${mobilePadding}px`, md: `${pl}px` },
        paddingRight:  { xs: `${mobilePadding}px`, md: `${pr}px` },
        maxWidth: maxWidth > 0 ? `${maxWidth}px` : 'none',
        width: '100%',
        height: '100%',
        order: { xs: mobileOrder, md: 'unset' },
        aspectRatio: {
          xs: mobileAspectRatio !== 'auto' ? mobileAspectRatio : undefined,
          md: aspectRatio !== 'auto' ? aspectRatio : undefined,
        },
        overflow: 'hidden',
      }}
    >
      {widgets.map(widget => renderWidget(widget))}
    </Box>
  );
};

// ─── Container Config ─────────────────────────────────────────────────────────

interface ContainerConfig {
  bgcolor?: string;
  centered?: boolean;
  columnGap?: number;
  height?: string;
  maxWidth?: string;
  mobileMaxWidth?: string;
  paddingTop?: number;
  paddingBottom?: number;
  paddingX?: number;
  mobilePaddingTop?: number;
  mobilePaddingBottom?: number;
  mobilePaddingX?: number;
}

// ─── Main Composed Block Renderer ─────────────────────────────────────────────

interface ComposedBlockRendererProps {
  layout: string;
  slots: Slot[];
  containerConfig?: ContainerConfig;
}

const ComposedBlockRenderer: React.FC<ComposedBlockRendererProps> = ({
                                                                       layout,
                                                                       slots,
                                                                       containerConfig = {},
                                                                     }) => {
  const {
    bgcolor = '',
    centered = false,
    columnGap = 16,
    height = '',
    maxWidth = '',
    mobileMaxWidth = '',
    paddingTop = 0,
    paddingBottom = 0,
    paddingX = 0,
    mobilePaddingTop = 0,
    mobilePaddingBottom = 0,
    mobilePaddingX = 0,
  } = containerConfig;

  const gridColumns = LAYOUT_COLUMNS[layout] ?? '1fr';

  const inner = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: gridColumns },
        gap: `${columnGap}px`,
        height: height || 'auto',
        width: '100%',
        bgcolor: bgcolor || 'transparent',
        paddingTop:    { xs: `${mobilePaddingTop}px`,    md: `${paddingTop}px` },
        paddingBottom: { xs: `${mobilePaddingBottom}px`, md: `${paddingBottom}px` },
        paddingLeft:   { xs: `${mobilePaddingX}px`,      md: `${paddingX}px` },
        paddingRight:  { xs: `${mobilePaddingX}px`,      md: `${paddingX}px` },
      }}
    >
      {slots.map(slot => (
        <SlotRenderer key={slot.id} slot={slot} />
      ))}
    </Box>
  );

  if (centered && (maxWidth || mobileMaxWidth)) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: {
            xs: mobileMaxWidth || maxWidth || '100%',
            md: maxWidth || '100%',
          },
          mx: 'auto',
        }}
      >
        {inner}
      </Box>
    );
  }

  if (centered) {
    return (
      <Box sx={{ width: '100%' }}>
        {inner}
      </Box>
    );
  }

  return inner;
};

export default ComposedBlockRenderer;